import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, signal, ChangeDetectionStrategy, Input } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormBuilder, ReactiveFormsModule, FormControlStatus } from '@angular/forms';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { CreateWorkOrderHeaderComponent } from '../../components/create-work-order/header/cwo-header.component';
import { CreateWorkOrerContainerDetailsComponent } from '../../components/create-work-order/container-details/cwo-container-details.component';
import { CreateWorkOrderAttachmentsComponent } from '../../components/create-work-order/attachments/cwo-attachments.component';
import { CreateWorkOrderLineItemsComponent } from '../../components/create-work-order/line-items/cwo-line-items.component';
import { CreateWorkOrderRemarksComponent } from '../../components/create-work-order/remarks/cwo-remarks.component';
import '@maersk-global/mds-components-core/mc-button';
import { CreateWorkOrderAlarmDetailsComponent } from '../../components/create-work-order/cwo-alarm-details/cwo-alarm-details.component';
import { WorkOrderService } from '../../services/work-order.service';
import { Mode } from '../../models/mode.model';
import { Shop } from '../../models/shop.model';
import { Equipment } from '../../models/equipment.model';
import { HeaderForm } from '../../form-model/cwo-header.form.model';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  forkJoin,
  ReplaySubject,
  Subscription,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { CodeDescription } from '../../models/code-description.model';
import { ContainerGrade } from '../../models/container-grade.model';
import { RepairLineItemsForm } from '../../form-model/damage-and-repair.form.model';
import { RemarkForm } from '../../form-model/cwo-remark.form.model';
import { CreateWorkOrderManHoursComponent } from '../../components/create-work-order/man-hours/cwo-man-hours/cwo-man-hours.component';
import { ManHoursForm } from '../../form-model/cwo-man-hours.form.model';
import { CreateWorkOrderTotalCostComponent } from '../../components/create-work-order/total-cost/cwo-total-cost/cwo-total-cost.component';
import { ToastrService } from '../../services/toastr-service';
import { utils } from '../../helper/utils';
import { AttachmentForm } from '../../form-model/attachment.form.model';
import { WorkOrderSubmitErrorModel } from '../../models/workorder-submit-error.model';
import { HttpErrorResponse } from '@angular/common/http';
import { TotalCostForm } from '../../form-model/total-cost.form.model';
import { ThirdPartyIndicator } from '../../models/tpi.model';
import { MaintenanceOrderResponse } from '../../models/maintenance-order-response.model';
import { TotalCost } from '../../models/total-cost-model';
import { RepairLocation } from '../../models/repair-location.model';
import { Repair } from '../../models/repair.model';
import { PrincipalProperty } from '../../models/principal-property.model';
import { clone, forEach } from 'lodash-es';
import { EditWorkOrderHeaderComponent } from '../../components/edit-work-order/header/edit-work-order-header/edit-work-order-header.component';
import { SharedDataService } from '../../services/shared-data.service';
import { APICallStatus } from '../../models/api-call-status.mode';
import { Currency } from '../../models/currency.model';
import { RepairExclusion } from '../../models/repair-exclusion.model';
import { PartExclusionRule } from '../../models/part-exclusion-rule.model';
import { RepairLineItemsDataService } from './repair-line-items-data.service';
import { CreateWorkOrderFormToRequestMapperService } from './create-work-order-form-to-request-mapper.service';
import { MasterDataService } from '../../services/master-data.service';
import { ShopRepairCostAllocation } from '../../models/shop-repair-cost-allocation.model';
import { ApplicationtTextService } from '../../services/application-text.service';
import { environment } from '../../../environments/environment';
import { MaintenanceOrderFilter } from '../../models/maintenance-order-filter.model';
import { MaintenanceOrderStatusCode } from '../../models/maintenance-order-status.model';
import { MaintenanceOrderHeader } from '../../models/maintenance-order-header.model';
import { InspectionDetail } from '../../models/inpection-detail.model';
import { AemsFile } from '../../models/aems-file.model';
import { v4 } from 'uuid';
@Component({
  selector: 'app-create-work-order',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    CreateWorkOrderHeaderComponent,
    CreateWorkOrerContainerDetailsComponent,
    CreateWorkOrderAttachmentsComponent,
    CreateWorkOrderLineItemsComponent,
    CreateWorkOrderManHoursComponent,
    CreateWorkOrderRemarksComponent,
    CreateWorkOrderAlarmDetailsComponent,
    CreateWorkOrderTotalCostComponent,
    ErrorComponent,
    NgxSkeletonLoaderModule,
    EditWorkOrderHeaderComponent,
  ],
  templateUrl: './create-work-order.component.html',
  styleUrl: './create-work-order.component.scss',
  providers: [RepairLineItemsDataService, CreateWorkOrderFormToRequestMapperService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CreateWorkOrderComponent implements OnInit {
  @Input() workorderId: string | undefined = undefined;

  // Properties for this component
  formSubmitErrors = signal<string[]>([]);
  headerFormValid = signal<boolean>(false);
  headerFormValidForDraftSave = signal<boolean>(false);
  enableDraftWorkOrder = signal(environment.Features.EnableDraftWorkOrder);
  submitLoader: boolean = false;
  saveAsDraftLoader: boolean = false;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  openCancelConfirmation: boolean = false;
  isEditMode = signal(false);
  workorder: MaintenanceOrderResponse | undefined;
  workOrderCurrency!: Currency;
  calculateTotalApiCallStatus: APICallStatus = 'NOT_STARTED';
  costCalculationErrorMessage: string = '';
  equipmentNumber: string | undefined;

  // Properties for man hours component
  totalManhoursWithoutPrepTime = 0;
  manHoursThatQualifyForPrepTime = 0;
  isPrepTimeApplicable = false;

  // Properties coming from header and must for the work order
  mode!: Mode;
  shop!: Shop;
  equipment!: Equipment;

  /** Tracks the current sales restriction exception reason. Set by the header component via @Output. */
  salesRestrictionExceptionReason: string | null = null;

  // Properties for repair line items components
  repairs: Repair[] = [];
  damages: CodeDescription[] = [];
  repairLocations: RepairLocation[] = [];
  existingDraft: MaintenanceOrderHeader[] = [];
  tpiCodes: ThirdPartyIndicator[] = [];
  containerGrades: ContainerGrade[] = [];
  repairExclusions: RepairExclusion[] = [];
  partExclusions: PartExclusionRule[] = [];
  repairLineItemsLoadFailed = signal(false);
  gettingDataFromAPIForRepairLineItemsForm = signal(true);
  repairLineItemsApiCallsSubscription: Subscription | undefined;
  inspectionDetails: InspectionDetail[] = [];
  selectedHeaderInspectionDamageImages: string[] = [];
  isHeaderInspectionDamageImagesInitialized = false;
  inspectionDetailsDelete = signal<string[]>([]);

  // The parent form of all the forms
  createWorkOrderForm = this.formBuilder.group({
    headerForm: this.formBuilder.group<HeaderForm>({} as HeaderForm),
    attachmentForm: this.formBuilder.group<AttachmentForm>({} as AttachmentForm),
    damageAndRepairForm: this.formBuilder.group<RepairLineItemsForm>({} as RepairLineItemsForm),
    manHoursForm: this.formBuilder.group<ManHoursForm>({} as ManHoursForm),
    remarkForm: this.formBuilder.group<RemarkForm>({} as RemarkForm),
    totalCostForm: this.formBuilder.group<TotalCostForm>({} as TotalCostForm),
  });

  // Properties for total cost component
  cost: TotalCost | null = null;
  repairLineItemsAndManhourFormValid = true;
  shopLimit!: ShopRepairCostAllocation;

  constructor(
    private workOrderService: WorkOrderService,
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private router: Router,
    private sharedDataService: SharedDataService,
    private repairLineItemDataService: RepairLineItemsDataService,
    private formToRequestBodyMapper: CreateWorkOrderFormToRequestMapperService,
    private masterDataService: MasterDataService,
    private appTextSvc: ApplicationtTextService,
  ) {}
  ngOnInit(): void {
    //this.warningSharedService.clearAllWarnings();

    /**
     * Only thing that effects man hours are the values in damage and repair form
     */
    this.createWorkOrderForm.controls.damageAndRepairForm.valueChanges.subscribe((value) => {
      this.calculateManHoursForRepairLineItems(value);
      this.saveAsDraftValidationCheck();
    });

    /**
     * Cost is effected by both damageAndRepair form and ManHours form
     */
    combineLatest([
      this.createWorkOrderForm.controls.damageAndRepairForm.valueChanges,
      this.createWorkOrderForm.controls.manHoursForm.valueChanges,
    ])
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged(), debounceTime(500))
      .subscribe(() => {
        this.saveAsDraftValidationCheck();
        this.calculateCost();
      });

    /**
     * If its an edit action then we will get workOrderid from the route parameter
     */
    if (this.workorderId) {
      this.sharedDataService.getWorkOrder().subscribe((workorder) => {
        if (workorder) {
          this.workorder = workorder;
          this.isEditMode.set(true);
          // Restore exception reason from DB so it is included if the user re-submits
          this.salesRestrictionExceptionReason = workorder.modeRestrictionExceptionReason ?? null;
        } else {
          this.router.navigate(['/workorders', this.workorderId]);
        }
      });
      this.workOrderService.getWorkOrderOrderExchangeRateInfoById(this.workorderId).subscribe((workorderCurrency) => {
        this.workOrderCurrency = workorderCurrency;
      });
    }
  }

  private calculateCost() {
    const reqBody = this.formToRequestBodyMapper.map(this.createWorkOrderForm, this.tpiCodes, this.equipment.equipmentTypeCode);
    if (
      this.createWorkOrderForm.controls.damageAndRepairForm.valid &&
      this.createWorkOrderForm.controls.manHoursForm.valid &&
      (this.isEditMode() || this.createWorkOrderForm.controls.damageAndRepairForm.touched)
    ) {
      this.repairLineItemsAndManhourFormValid = true;
      this.calculateTotalApiCallStatus = 'IN_PROGRESS';
      this.workOrderService
        .calculateTotalCost(this.workorderId, reqBody)
        .pipe(
          catchError((err) => {
            this.calculateTotalApiCallStatus = 'FAILED';
            if (err.status === 400 && err.error?.errorMessage) {
              const message = err.error.errorMessage;
              if (message.includes('Labour rate')) {
                // Extract the shop code using regex
                const match = message.match(/'([^']+)'/); // matches anything inside single quotes
                const shopCode = match ? match[1] : 'N/A';
                this.costCalculationErrorMessage = this.appTextSvc.labourRateContractExpired(shopCode);
              } else {
                this.costCalculationErrorMessage = message;
              }
            }
            throw err;
          }),
        )
        .subscribe((cost) => {
          this.cost = clone(cost);
          this.calculateTotalApiCallStatus = 'COMPLETED_SUCCESFULLY';
          this.costCalculationErrorMessage = '';
          this.compareTotalCostWithShopLimit(this.cost.totalOrderCostWithTax, this.shopLimit);
        });
    } else {
      this.repairLineItemsAndManhourFormValid = false;
      this.calculateTotalApiCallStatus = 'NOT_STARTED';
    }
  }

  private compareTotalCostWithShopLimit(totalCost: number, shopLimit: ShopRepairCostAllocation) {
    if (shopLimit && shopLimit.maxOrderCost != 0 && totalCost > shopLimit.maxOrderCost) {
      this.toastrService.showToastr(
        `The total cost of the work order is more than the shop material limit set for given mode '${this.mode.code}' and shop code '${this.shop.maintenanceShopCode}'`,
        'warning',
        { duration: 60000 },
      );
    }
  }

  private calculateManHoursForRepairLineItems(value: typeof this.createWorkOrderForm.controls.damageAndRepairForm.value) {
    this.totalManhoursWithoutPrepTime = 0;
    this.manHoursThatQualifyForPrepTime = 0;
    this.isPrepTimeApplicable = false;
    value.repairLineItems?.forEach((repairLineItem) => {
      let manhours = +(repairLineItem?.manHoursPerPiece ?? 0);
      let pieces = +(repairLineItem?.pieces ?? 0);
      this.totalManhoursWithoutPrepTime = this.totalManhoursWithoutPrepTime + manhours * pieces;
      if (this.shop.isPrepTimeEnabled) {
        let repair = this.repairs.find((x) => x.repairCode === repairLineItem?.repairCode);
        if (repair && !repair.excludePreparationHours) {
          this.manHoursThatQualifyForPrepTime = this.manHoursThatQualifyForPrepTime + manhours * pieces;
          this.isPrepTimeApplicable = true;
        }
      }
    });
    this.totalManhoursWithoutPrepTime = utils.formatNumberToTwoDigitDecimal(this.totalManhoursWithoutPrepTime);
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onSubmitClicked(scrollToElementOnError: HTMLHeadElement) {
    if (this.createWorkOrderForm.valid) {
      this.submitWorkOrder(scrollToElementOnError);
    }
  }

  onCancelClicked() {
    if (this.createWorkOrderForm.dirty) {
      this.openCancelConfirmation = true;
    } else {
      this.router.navigate(['/workorders']);
    }
  }
  onCancelConfirmationClosed() {
    this.openCancelConfirmation = false;
  }
  onCancelRejection() {
    this.openCancelConfirmation = false;
  }
  onCancelConfirmation() {
    this.router.navigate(['/workorders']);
    this.openCancelConfirmation = false;
  }
  private submitWorkOrder(scrollTo: HTMLHeadElement) {
    this.submitLoader = true;
    const reqBody = this.formToRequestBodyMapper.map(
      this.createWorkOrderForm,
      this.tpiCodes,
      this.equipment.equipmentTypeCode,
      undefined,
      this.salesRestrictionExceptionReason,
    );
    reqBody.maintenanceOrderNumber = this.workorderId;
    this.workOrderService
      .createOrUpdateWorkOrder(reqBody, this.workorderId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: MaintenanceOrderResponse) => {
          if (response) {
            this.createWorkOrderForm.reset();
            this.formSubmitErrors.set([]);
            if (this.workorderId) {
              this.toastrService.showToastr(this.appTextSvc.WorkOrderResubmittedSuccessfully, 'success');
            } else {
              this.toastrService.showToastr(this.appTextSvc.WorkOrderSubmittedSuccessfully, 'success');
            }
            this.submitLoader = false;
            this.router.navigate(['/workorders', response.maintenanceOrderNumber]);
          }
        },
        error: (error: HttpErrorResponse) => {
          let errors: WorkOrderSubmitErrorModel = error.error;
          this.formSubmitErrors.set(['Error Id: ' + errors.correlationIdentifier]);
          if (error.status === 422) {
            errors?.violations?.every((error) =>
              this.formSubmitErrors.update((existingErrors) => {
                existingErrors.push(error.message);
                return existingErrors;
              }),
            );
          } else {
            this.formSubmitErrors.set(['Error Id: ' + errors.correlationIdentifier, errors.errorMessage]);
          }
          this.submitLoader = false;
          this.toastrService.showToastr(this.appTextSvc.WorkOrderSubmittedFailed, 'error');
          scrollTo.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        },
      });
  }

  modeChanged(event: Mode) {
    this.mode = clone(event);
    this.formSubmitErrors.set([]);
  }

  onSalesRestrictionOverrideChanged(reason: string | null): void {
    this.salesRestrictionExceptionReason = reason;
  }

  shopChanged(event: Shop) {
    this.shop = clone(event);
    this.workOrderCurrency = this.shop.maintenanceShopCurrency;
    this.formSubmitErrors.set([]);
  }

  headerFormValidChange(event: FormControlStatus) {
    this.headerFormValid.set(false);
    event === 'VALID' ? this.headerFormValid.set(true) : this.headerFormValid.set(false);
  }

  saveAsDraftValidationCheck() {
    const reqBody = this.formToRequestBodyMapper.map(this.createWorkOrderForm, this.tpiCodes, this.equipment.equipmentTypeCode, 10);
    if (this.createWorkOrderForm.invalid === false || reqBody.maintenanceOrderLines.length === 0) {
      this.headerFormValidForDraftSave.set(true);
    } else if (
      reqBody.maintenanceOrderLines.length == 1 &&
      reqBody.maintenanceOrderLines[0].equipmentDamageCode === '' &&
      reqBody.maintenanceOrderLines[0].maintenanceTypeCode === ''
    ) {
      this.createWorkOrderForm.controls.manHoursForm.setValue({
        preparationTime: 0,
        totalManHours: 0.1,
        ordinaryHours: 0.1,
        totalManHoursOvertime: 0,
        totalManHoursDoubletime: 0,
        totalManHoursMisc: 0,
      });
      this.cost = null;
      this.totalManhoursWithoutPrepTime = 0;
      this.manHoursThatQualifyForPrepTime = 0;

      this.headerFormValidForDraftSave.set(true);
    } else {
      this.headerFormValidForDraftSave.set(false);
    }
  }

  equipmentChanged(event: Equipment) {
    this.equipment = clone(event);
    this.createWorkOrderForm.controls.remarkForm.reset();
  }

  async principalPropertyChanged(event: PrincipalProperty) {
    this.createWorkOrderForm.controls.remarkForm.reset();
    if (event.HeaderFormStatus === 'VALID') {
      this.gettingDataFromAPIForRepairLineItemsForm.set(true);
      this.repairLineItemsLoadFailed.set(false);
      this.repairLineItemsApiCallsSubscription && this.repairLineItemsApiCallsSubscription.unsubscribe();
      this.equipmentNumber = event.Equipment.equipmentNumber ?? '';
      // Set fromDateUTC to 15 days ago
      const fromDate = new Date();
      fromDate.setDate(new Date().getDate() - 15);
      const fromDateUTC: string = fromDate.toISOString();

      const toDateUTC: string = new Date().toISOString();
      const filters: MaintenanceOrderFilter = {};
      filters.equipmentNumber = [event.Equipment.equipmentNumber ?? ''];
      filters.maintenanceRepairModes = event.Mode.code;
      filters.maintenanceShopCodes = event.Shop.maintenanceShopCode;
      filters.maintenanceOrderStatusTypes = `${MaintenanceOrderStatusCode.Draft}`;
      this.repairLineItemsApiCallsSubscription = forkJoin([
        this.repairLineItemDataService.getRepairs(event.Shop.maintenanceShopCode, event.Mode.code, this.workOrderCurrency),
        this.repairLineItemDataService.getRepairExclusions(event.Mode.code),
        this.masterDataService.getPartExclusions(event.Mode.code).pipe(take(1)),
        this.repairLineItemDataService.getContainerGrades(),
        this.repairLineItemDataService.getDamages(event.Mode.damageType),
        this.repairLineItemDataService.getTPIs(),
        this.repairLineItemDataService.getRepairLocations(),
        this.masterDataService.getShopRepairCostLimitByMode(event.Shop.maintenanceShopCode, event.Mode.code).pipe(take(1)),
        this.workOrderService.getWorkOrders(1, 1, 'createTimestamp:asc', filters),
        this.masterDataService
          .getEquipmentInspectionDetails(event.Shop.maintenanceShopCode, event.Equipment.equipmentNumber ?? '', fromDateUTC, toDateUTC)
          .pipe(take(1)),
      ])
        .pipe(
          catchError((err) => {
            this.repairs = [];
            this.repairExclusions = [];
            this.partExclusions = [];
            this.containerGrades = [];
            this.damages = [];
            this.tpiCodes = [];
            this.repairLocations = [];
            this.existingDraft = [];
            this.inspectionDetails = [];
            this.gettingDataFromAPIForRepairLineItemsForm.set(false);
            this.repairLineItemsLoadFailed.set(true);
            this.toastrService.showToastr('Failed to load repair line item data, including part exclusions', 'error');
            return EMPTY;
          }),
        )
        .subscribe(
          ([
            repairs,
            repairExclusions,
            partExclusions,
            containerGrades,
            damages,
            tpiCodes,
            repairLocations,
            shopRepairCostAlocation,
            existingDraftResponse,
            inspectionDetail,
          ]) => {
            const activeRepairs = repairs.filter((repair) => repair.isActive && !repair.isSuspended);
            const activeRepairsAfterContainerSizeRestriction = this.repairLineItemDataService.filterRepairCodesByEquipmentSize(
              activeRepairs,
              event.Equipment,
            );
            this.repairs = this.repairLineItemDataService.filterRepairCodesByEquipmentManufacturerAndModel(
              activeRepairsAfterContainerSizeRestriction,
              event.Equipment,
              event.Mode.damageType,
            );
            this.repairExclusions = repairExclusions;
            this.partExclusions = partExclusions;
            this.containerGrades = containerGrades;
            this.damages = damages;
            this.tpiCodes = tpiCodes;
            this.repairLocations = repairLocations.filter((x) => x.damageType === event.Mode.damageType);
            this.shopLimit = shopRepairCostAlocation;
            this.existingDraft = existingDraftResponse?.body || [];
            this.inspectionDetails = this.initializeInspectionDetailsWithGuids(inspectionDetail ?? []);
            if (this.existingDraft.length > 0 && !this.isEditMode()) {
              const workOrderId = this.existingDraft[0].maintenanceOrderNumber;
              const baseUrl = window.location.origin;
              const linkUrl = `${baseUrl}/workorders/${workOrderId}`;
              this.toastrService.showToastr('', 'warning', {
                duration: 60000,
                htmlMessage: `A draft already exists for the selected equipment and mode. You may either submit the work order or update the draft provided.
              <a href="${linkUrl}" target="_blank">${workOrderId}</a>`,
              });
            }
            timer(500).subscribe(() => this.gettingDataFromAPIForRepairLineItemsForm.set(false));
          },
        );
    }
  }

  onSaveAsDraftClicked(scrollTo: HTMLElement): void {
    this.saveAsDraftLoader = true;
    const reqBody = this.formToRequestBodyMapper.map(
      this.createWorkOrderForm,
      this.tpiCodes,
      this.equipment.equipmentTypeCode,
      10,
      this.salesRestrictionExceptionReason,
    );
    if (
      reqBody.maintenanceOrderLines.length == 1 &&
      reqBody.maintenanceOrderLines[0].equipmentDamageCode === '' &&
      reqBody.maintenanceOrderLines[0].maintenanceTypeCode === ''
    ) {
      reqBody.maintenanceOrderLines = [];
      this.headerFormValidForDraftSave.set(true);
    }
    reqBody.maintenanceOrderNumber = this.workorderId;
    this.workOrderService
      .saveOrUpdateDraftWorkOrder(reqBody, this.workorderId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: MaintenanceOrderResponse) => {
          if (response) {
            this.createWorkOrderForm.reset();
            this.formSubmitErrors.set([]);
            if (this.workorderId) {
              this.toastrService.showToastr(this.appTextSvc.workOrderResavedAsDraftSuccessfully, 'success');
            } else {
              this.toastrService.showToastr(this.appTextSvc.workOrderSavedAsDraftSuccessfully, 'success');
            }
            this.saveAsDraftLoader = false;
            this.router.navigate(['/workorders', response.maintenanceOrderNumber]);
          }
        },
        error: (error: HttpErrorResponse) => {
          let errors: WorkOrderSubmitErrorModel = error.error;
          this.formSubmitErrors.set(['Error Id: ' + errors.correlationIdentifier]);
          if (error.status === 422) {
            errors?.violations?.every((error) =>
              this.formSubmitErrors.update((existingErrors) => {
                existingErrors.push(error.message);
                return existingErrors;
              }),
            );
          } else {
            this.formSubmitErrors.set(['Error Id: ' + errors.correlationIdentifier, errors.errorMessage]);
          }
          this.saveAsDraftLoader = false;
          this.toastrService.showToastr(this.appTextSvc.failedToWOAsSaveDraft, 'error');
          scrollTo.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        },
      });
  }

  get headerAttachment(): AemsFile[] {
    let filteredFiles = [...(this.workorder?.blobResolvedFiles ?? [])];
    let inspectionDetailsWithGuid = this.getInspectionDetailsWithUpdatedGuid();
    if (this.isEditMode() && inspectionDetailsWithGuid.length > 0) {
      forEach(inspectionDetailsWithGuid, (report) => {
        let generatedName = report.guid ?? '';
        filteredFiles = filteredFiles.filter((f) => f.generatedName !== generatedName);
        forEach(report.damageImages, (image) => {
          let imageName = image.guid ?? '';
          filteredFiles = filteredFiles.filter((f) => f.generatedName !== imageName);
        });
      });

      return filteredFiles;
    }
    return filteredFiles;
  }

  private initializeInspectionDetailsWithGuids(inspectionDetails: InspectionDetail[]): InspectionDetail[] {
    return inspectionDetails.map((detail) => {
      detail.guid = utils.generateFileNameWithIdAndExtension(v4(), detail.inspectionReportURL);
      detail.damageImages = detail.damageImages.map((image) => {
        image.guid = utils.generateFileNameWithIdAndExtension(v4(), image.path);
        return image;
      });
      return detail;
    });
  }

  getInspectionDetailsWithUpdatedGuid(): InspectionDetail[] {
    const currentInspectionDetails = this.inspectionDetails;
    let hasAnyUpdates = false;
    let headerInspectionDamageImages: string[] = [];

    const updatedDetails = currentInspectionDetails.map((detail) => {
      const updatedDetail = { ...detail };

      let headerFiles = [...(this.workorder?.blobResolvedFiles ?? [])];
      let eEIRReport = headerFiles?.find((x) => utils.getEirImageid(x.displayName) === detail.inspectionId.toString());

      if (eEIRReport) {
        updatedDetail.guid = eEIRReport.generatedName;
      }

      updatedDetail.damageImages = detail.damageImages.map((image) => {
        let imageWithGuid = { ...image };
        let eEIRDamageImage = headerFiles?.find((x) => utils.getEirImageid(x.displayName) === imageWithGuid.id.toString());

        if (eEIRDamageImage) {
          imageWithGuid.guid = eEIRDamageImage.generatedName;
          imageWithGuid.isLinked = true;
          imageWithGuid.linkedLineItemIdentifier = this.equipmentNumber ?? '';
          hasAnyUpdates = true;
          headerInspectionDamageImages.push(imageWithGuid.guid ?? '');
        }
        return imageWithGuid;
      });

      return updatedDetail;
    });

    if (hasAnyUpdates) {
      this.inspectionDetails = updatedDetails;
    }
    if (!this.isHeaderInspectionDamageImagesInitialized) {
      this.selectedHeaderInspectionDamageImages = headerInspectionDamageImages;
      this.isHeaderInspectionDamageImagesInitialized = true;
    }

    return updatedDetails;
  }

  onInspectionImageDelete(deleteEvent: InspectionDetail[]) {
    const unlinkedImageGuids: string[] = [];
    deleteEvent.forEach((deleteDetail) => {
      deleteDetail.damageImages.forEach((deleteImage) => {
        if (
          (deleteImage.isLinked === false || deleteImage.isLinked === undefined) &&
          deleteImage.guid &&
          this.selectedHeaderInspectionDamageImages.includes(deleteImage.guid)
        ) {
          unlinkedImageGuids.push(deleteImage.guid);
        }
      });
    });

    this.inspectionDetailsDelete.set(unlinkedImageGuids);

    setTimeout(() => {
      this.inspectionDetailsDelete.set([]);
    }, 300);

    this.selectedHeaderInspectionDamageImages = this.selectedHeaderInspectionDamageImages.filter(
      (guid) => !unlinkedImageGuids.includes(guid),
    );
  }
}
